import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Building2, Plane, FileText, ArrowRight, Lock as LockIcon } from 'lucide-react';
import DashboardContext from "../../../../../../context/dashboard/DashboardContext";
import PlanGate, { useVoucherLock } from "../../../../../../components/gating/PlanGate";

// Internal Components
import GenericUploadZone from "./GenericUploadZone";

const MAX_TOTAL_FILES = 30;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

function AiVoucherCreator({
    initialFiles = [],
    initialHotelFiles = [],
    initialTravelFiles = [],
    initialItineraryFiles = [],
    onSuccess,
    onSwitchToManual
}) {
    const navigate = useNavigate();
    const { isLocked: isVoucherLocked } = useVoucherLock();
    const [hotelFiles, setHotelFiles] = useState([]);
    const [travelFiles, setTravelFiles] = useState([]);
    const [itineraryFiles, setItineraryFiles] = useState([]);

    const [isHotelDragging, setIsHotelDragging] = useState(false);
    const [isTravelDragging, setIsTravelDragging] = useState(false);
    const [isItineraryDragging, setIsItineraryDragging] = useState(false);

    const travelZoneRef = useRef(null);
    const itineraryZoneRef = useRef(null);

    const { 
        analysisState, 
        startVoucherAnalysis,
        resetAnalysis: resetGlobalAnalysis 
    } = useContext(DashboardContext);

    // Helper for validation
    const validateFiles = (incomingFiles, currentZoneFiles, otherZonesFiles = []) => {
        const totalOtherCount = otherZonesFiles.reduce((acc, curr) => acc + curr.length, 0);
        const totalCount = currentZoneFiles.length + totalOtherCount;
        
        if (totalCount >= MAX_TOTAL_FILES) {
            toast.error(`Maximum limit of ${MAX_TOTAL_FILES} files reached.`);
            return [];
        }

        const validTypes = incomingFiles.filter(file =>
            file.type === 'application/pdf' || 
            file.type.startsWith('image/') || 
            file.type.startsWith('video/')
        );

        if (validTypes.length < incomingFiles.length) {
            toast.error("Some files were skipped. Only PDFs, Images, and Videos are allowed.");
        }

        const sizeChecked = validTypes.filter(file => {
            if (file.size > MAX_FILE_SIZE) {
                toast.error(`${file.name} exceeds 50MB limit.`);
                return false;
            }
            return true;
        });

        const remainingSlots = MAX_TOTAL_FILES - totalCount;
        if (sizeChecked.length > remainingSlots) {
            toast.error(`Only ${remainingSlots} more files can be added.`);
            return sizeChecked.slice(0, remainingSlots);
        }

        return sizeChecked;
    };

    // Auto-scroll logic (only on addition)
    const prevHotelLen = useRef(0);
    const prevTravelLen = useRef(0);

    useEffect(() => {
        if (hotelFiles.length > prevHotelLen.current && hotelFiles.length > 0 && travelFiles.length === 0) {
            travelZoneRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        prevHotelLen.current = hotelFiles.length;
    }, [hotelFiles.length, travelFiles.length]);

    useEffect(() => {
        if (travelFiles.length > prevTravelLen.current && travelFiles.length > 0 && itineraryFiles.length === 0) {
            itineraryZoneRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        prevTravelLen.current = travelFiles.length;
    }, [travelFiles.length, itineraryFiles.length]);

    // Handle initial files (Global Drop / Navigation)
    useEffect(() => {
        const hotelIncoming = (initialHotelFiles && initialHotelFiles.length > 0) ? initialHotelFiles : initialFiles;
        if (hotelIncoming && hotelIncoming.length > 0) {
            setHotelFiles(prev => {
                const incoming = validateFiles(hotelIncoming, prev, [travelFiles, itineraryFiles]);
                const newFiles = [...prev, ...incoming];
                return newFiles.filter((file, index, self) =>
                    index === self.findIndex((f) => (
                        f.name === file.name && f.size === file.size
                    ))
                );
            });
        }
        if (initialTravelFiles && initialTravelFiles.length > 0) {
            setTravelFiles(prev => {
                const incoming = validateFiles(initialTravelFiles, prev, [hotelFiles, itineraryFiles]);
                const newFiles = [...prev, ...incoming];
                return newFiles.filter((file, index, self) =>
                    index === self.findIndex((f) => (
                        f.name === file.name && f.size === file.size
                    ))
                );
            });
        }
        if (initialItineraryFiles && initialItineraryFiles.length > 0) {
            setItineraryFiles(prev => {
                const incoming = validateFiles(initialItineraryFiles, prev, [hotelFiles, travelFiles]);
                const newFiles = [...prev, ...incoming];
                return newFiles.filter((file, index, self) =>
                    index === self.findIndex((f) => (
                        f.name === file.name && f.size === file.size
                    ))
                );
            });
        }
    }, [initialFiles, initialHotelFiles, initialTravelFiles, initialItineraryFiles]);

    // Handle Handoff to Manual Editor
    useEffect(() => {
        if (analysisState.result && analysisState.analysisType === 'initial' && onSuccess) {
            toast.success("Analysis complete! Opening Editor...");
            onSuccess(analysisState.result);
            setHotelFiles([]);
            setTravelFiles([]);
            setItineraryFiles([]);
            resetGlobalAnalysis();
        }
    }, [analysisState.result, analysisState.analysisType, onSuccess, resetGlobalAnalysis]);

    const createDropHandler = (setter, currentFiles, otherZones) => (e) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files);
        const validFiles = validateFiles(droppedFiles, currentFiles, otherZones);

        if (validFiles.length > 0) {
            setter(prev => {
                const existingNames = new Set(prev.map(f => f.name + f.size));
                const newFiles = validFiles.filter(f => !existingNames.has(f.name + f.size));
                if (newFiles.length < validFiles.length) {
                    toast.error("Duplicate files were ignored.");
                }
                return [...prev, ...newFiles];
            });
        }
    };

    const handleGenerate = () => {
        if (isVoucherLocked) {
            navigate('/subscription');
            toast.error('Active subscription or trial required to create vouchers. Upgrade to unlock.');
            return;
        }
        if (hotelFiles.length === 0) {
            toast.error("Hotel Booking confirmation is mandatory.");
            return;
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const docFiles = [...hotelFiles, ...travelFiles];
        startVoucherAnalysis(docFiles, itineraryFiles);
    };

    return (
        <div className="w-full relative flex-1 flex flex-col justify-between space-y-6 min-h-0">
            {/* 3 Upload Zones in Single-View Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch flex-1 min-h-0">
                <GenericUploadZone 
                    id="hotel-upload"
                    stepBadge="STEP 1 • MANDATORY"
                    title="Hotel Bookings"
                    subtitle="Upload {highlight}Hotel{highlight} Confirmations"
                    description="Drop hotel booking PDFs or screenshots. Required for room & guest details."
                    icon={Building2}
                    colorClass="teal"
                    files={hotelFiles}
                    setFiles={setHotelFiles}
                    handleDragOver={(e) => { e.preventDefault(); setIsHotelDragging(true); }}
                    handleDragLeave={(e) => { e.preventDefault(); setIsHotelDragging(false); }}
                    handleDrop={(e) => { setIsHotelDragging(false); createDropHandler(setHotelFiles, hotelFiles, [travelFiles, itineraryFiles])(e); }}
                    isDragging={isHotelDragging}
                    isSubmitting={analysisState.isAnalyzing}
                />

                <GenericUploadZone 
                    id="travel-upload"
                    containerRef={travelZoneRef}
                    stepBadge="STEP 2 • OPTIONAL"
                    title="Travel Tickets"
                    subtitle="Upload {highlight}Travel{highlight} Tickets"
                    description="Flight, train, or bus vouchers. Auto-extracts dates and timings."
                    icon={Plane}
                    colorClass="teal"
                    files={travelFiles}
                    setFiles={setTravelFiles}
                    handleDragOver={(e) => { e.preventDefault(); setIsTravelDragging(true); }}
                    handleDragLeave={(e) => { e.preventDefault(); setIsTravelDragging(false); }}
                    handleDrop={(e) => { setIsTravelDragging(false); createDropHandler(setTravelFiles, travelFiles, [hotelFiles, itineraryFiles])(e); }}
                    isDragging={isTravelDragging}
                    isSubmitting={analysisState.isAnalyzing}
                />

                <GenericUploadZone 
                    id="itinerary-upload"
                    containerRef={itineraryZoneRef}
                    stepBadge="STEP 3 • OPTIONAL"
                    title="Detailed Itinerary"
                    subtitle="Upload {highlight}Detailed{highlight} Plan"
                    description="Day-by-day itinerary or package PDF to enrich schedule & activities."
                    icon={FileText}
                    colorClass="teal"
                    files={itineraryFiles}
                    setFiles={setItineraryFiles}
                    handleDragOver={(e) => { e.preventDefault(); setIsItineraryDragging(true); }}
                    handleDragLeave={(e) => { e.preventDefault(); setIsItineraryDragging(false); }}
                    handleDrop={(e) => { setIsItineraryDragging(false); createDropHandler(setItineraryFiles, itineraryFiles, [hotelFiles, travelFiles])(e); }}
                    isDragging={isItineraryDragging}
                    isSubmitting={analysisState.isAnalyzing}
                />
            </div>

            {/* Pill Outline Generate Voucher Button & Manual Fallback */}
            <div className="w-full max-w-md pt-2 pb-4 mx-auto shrink-0 flex flex-col items-center">
                <PlanGate
                    featureKey="voucher-creation"
                    featureName="Voucher Creation"
                    tooltipText="Subscription required to create vouchers."
                    showLockIcon={false}
                    wrapperClassName="w-full"
                >
                    {({ isLocked }) => (
                        <button
                            onClick={handleGenerate}
                            disabled={!isLocked && (hotelFiles.length === 0 || analysisState.isAnalyzing)}
                            className="w-full py-3.5 px-8 rounded-full bg-[#067978] hover:bg-[#056362] text-white font-bold text-base shadow-lg shadow-teal-700/20 hover:shadow-xl hover:shadow-teal-700/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none cursor-pointer flex items-center justify-center gap-3 group"
                        >
                            {analysisState.isAnalyzing ? (
                                <span className="flex items-center justify-center gap-2.5">
                                    <span className="animate-spin w-5 h-5 border-2 border-white/30 border-t-white rounded-full" />
                                    <span>Processing documents...</span>
                                </span>
                            ) : (
                                <span className="flex items-center justify-center gap-2.5">
                                    <span>Generate Voucher</span>
                                    {isLocked ? (
                                        <LockIcon size={18} className="ml-1 text-white/90 shrink-0" />
                                    ) : (
                                        <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
                                    )}
                                </span>
                            )}
                        </button>
                    )}
                </PlanGate>

                {onSwitchToManual && (
                    <button
                        type="button"
                        onClick={onSwitchToManual}
                        className="mt-3 text-slate-400 hover:text-[#067978] text-xs font-semibold underline underline-offset-4 cursor-pointer transition-colors"
                    >
                        or enter voucher details manually
                    </button>
                )}
            </div>
        </div>
    );
}

export default AiVoucherCreator;
